import { Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'

import { isSomething, Rental, selectOnce, selectRentalById, Toaster, userFullname } from '@tokeet-frontend/tv3-platform'
import { MessageService } from '@tv3/store/message/message.service'
import {
  AddMessage,
  AddMessageComplete,
  DeleteMessage,
  DeleteMessageComplete,
  DeleteMessages,
  DeleteMessagesComplete,
  GetBookingMessages,
  GetBookingMessagesComplete,
  GetBookingMessagesFailed,
  GetGuestMessages,
  GetGuestMessagesComplete,
  GetMessagesByIds,
  GetMessagesByIdsComplete,
  LoadLatestMessages,
  LoadLatestMessagesComplete,
  LoadUnreadCount,
  LoadUnreadCountComplete,
  MarkAsRead,
  MarkAsReadComplete,
  MarkAsUnread,
  MarkAsUnreadComplete,
  MarkOneMessageAsRead,
  MarkOneMessageAsReadComplete,
  MoveMessage,
  MoveMessageComplete,
  MoveMessagesInConversation,
  MoveMessagesInConversationComplete,
  RemoveMessageFromFeed,
  RemoveMessageFromFeedComplete,
  RemoveMessagesFromFeed,
  RemoveMessagesFromFeedComplete,
  SendQuoteMessage,
  UpdateMessage,
  UpdateMessageComplete,
} from '@tv3/store/message/message.actions'
import { catchError, concatMap, map, switchMap, take, tap } from 'rxjs/operators'
import { combineLatest, of } from 'rxjs'
import { ActionFailed } from '@tokeet-frontend/tv3-platform'
import { ChannelMessagePayload, MessageRequest } from '@tv3/store/message/message.request'
import * as R from 'ramda'
import { Inquiry } from '@tv3/store/inquiry/inquiry.model'
import { Message, MessageType } from '@tv3/store/message/message.model'
import { select, Store } from '@ngrx/store'
import * as fromRoot from '@tv3/store/state'
import { selectGuestById } from '@tv3/store/guest/guest.selectors'
import { InquiryChargeService } from '@tv3/services/inquiry-charge.service'
import { selectBrandingPreference } from '@tv3/store/preferences/preferences.selectors'
import { MessageForm } from '@tv3/interfaces/forms/message.form'
import { getMessagingChannelById, MessagingChannelType } from '@tokeet-frontend/message'
import moment from 'moment'
import { AuthService } from '@tv3/services/auth.service'
import { CurrencyPipe } from '@angular/common'
import { InquiryCost, parseBookingCostString } from '@tokeet/cost-resolver'
import { Guest } from '../guest/guest.model'
import { SendMessageToMultipleChannels } from './message.actions'
import { Branding } from '@tv3/models/account/branding'
import * as lodash from 'lodash'

@Injectable()
export class MessageEffects {
  loadLatest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadLatestMessages),
      switchMap(({ messageTypes, lastDays, maxPages }) =>
        this.messageService.getLatestByTypes(messageTypes, lastDays, maxPages).pipe(
          map((messages) => LoadLatestMessagesComplete({ messages })),
          catchError((error) => of(ActionFailed({ error })))
        )
      )
    )
  )

  loadUnreadCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadUnreadCount),
      switchMap(() =>
        this.messageService.getUnreadCount().pipe(
          map((count) => LoadUnreadCountComplete({ count })),
          catchError((error) => of(ActionFailed({ error })))
        )
      )
    )
  )

  getGuestMessages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetGuestMessages),
      concatMap(({ guestId, background }) =>
        this.messageService.getMessagesByGuestId(guestId, 200, 0, background).pipe(
          map((messages) => GetGuestMessagesComplete({ messages, guestId })),
          catchError((error) => of(ActionFailed({ error })))
        )
      )
    )
  )

  private getNewMessagePayload(inquiry: Inquiry, data: MessageForm) {
    return combineLatest([
      this.store.pipe(select(selectGuestById, { id: inquiry.guestId })),
      this.store.pipe(select(selectBrandingPreference)),
      this.store.pipe(select(selectRentalById(inquiry.rentalId))),
      data.addCost ? this.inquiryChargeService.getCharges(inquiry) : of(null),
    ]).pipe(
      take(1),
      map(([guest, branding, rental, charges]: [Guest, Branding, Rental, InquiryCost | null]) => {
        const request: MessageRequest = {
          inquiry_id: inquiry.id,
          convo_id: inquiry.convoId,
          guest_id: inquiry.guestId,
          rental_id: inquiry.rentalId,
          ref_id: inquiry.refId,
          api: R.pathOr('', ['attributes', 'api'], inquiry),
          source: inquiry.inquirySource,
          message_txt: data.messageText,
          message_html: data.messageHtml,
          rental_name: rental?.name,
          guest_name: guest?.name || R.pathOr('', ['guestDetails', 'name'], inquiry),
          guest_email: guest?.primaryEmail || R.pathOr('', ['guestDetails', 'email'], inquiry),
          guest_arrive: inquiry.guestArrive,
          guest_depart: inquiry.guestDepart,
          cc: data.cc || [],
          bcc: data.bcc || [],
          subject: lodash.trim(data.subject || $localize`Re: Your inquiry about ` + (rental?.name || '')),
          from: rental?.email,
          attachments: data.attachments,
        }

        // @todo - this shouldn't be done here but on the backend
        if (charges && data.addCost) {
          request.charge = (charges.charge || 0).toFixed(2)
          request.discounts = charges.discountSum + charges.feeSum
          request.tax_fee = charges.taxFee
          request.sum = charges.sum
          request.quote = 1
          request.template = branding?.quoteTemplate || '1112223336ST-2'
          request.currency = rental?.currency?.code || ''
        } else {
          request.template = branding?.standardTemplate || '1112223337ST-2'
        }
        return request
      })
    )
  }

  private getNewChannelMessagePayload(
    channel: MessagingChannelType,
    data: MessageRequest,
    form: MessageForm,
    accountId: number
  ): ChannelMessagePayload {
    const payload: ChannelMessagePayload = {
      name: data.rental_name || 'AdvanceCM',
      guest_id: data.guest_id,
      inquiry_id: data.inquiry_id,
      convo_id: data.convo_id,
      rental_id: data.rental_id,
      channel: getMessagingChannelById(channel)?.channel,
      sender: {
        account: String(accountId),
      },
      receiver: {
        email: decodeURIComponent(data.guest_email),
        'phone-number': channel === MessagingChannelType.Whatsapp ? form.whatsapp_phone : form.phone,
      },
      'message-data': {
        content: data.message_txt,
      },
    }

    if (channel === MessagingChannelType.Whatsapp && form.whatsappTemplateId) {
      payload['message-data'] = {
        content_id: form.whatsappTemplateId,
        content_variables: form.whatsappTemplateVariables,
      }
    }

    if (channel.startsWith(MessagingChannelType.Gmail)) {
      payload['message-data']['email-subject'] = form.subject
    }

    return payload
  }

  addMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AddMessage),
      concatMap(({ inquiry, form, channel }) =>
        this.getNewMessagePayload(inquiry, form).pipe(map((request) => ({ request, inquiry, channel, form })))
      ),
      concatMap(({ request, inquiry, channel, form }) => {
        const isEmailMessage = channel === MessagingChannelType.Email
        return (
          isEmailMessage
            ? this.messageService.create(request)
            : this.messageService.createChannelMessage(
                this.getNewChannelMessagePayload(channel, request, form, inquiry.account)
              )
        ).pipe(
          map((response) => {
            const message = response.id
              ? response
              : Message.deserialize({
                  ...request,
                  type: MessageType.Outbound,
                  name: userFullname(this.auth.user),
                  received_on: moment().unix(),
                  created: moment().unix(),
                  pkey: btoa(new Date().toISOString()),
                  sent_with: isEmailMessage ? 'tokeet' : channel,
                })

            return { isSending: !response.id, message }
          }),
          tap(({ isSending }) =>
            isSending
              ? this.toast.info($localize`Message is being sent...`)
              : this.toast.success($localize`Message sent successfully!`)
          ),
          switchMap(({ message }) => {
            const actions: any[] = [AddMessageComplete({ message })]
            return actions
          }),
          catchError((error) => of(ActionFailed({ error })))
        )
      })
    )
  )

  private getBookingQuoteMessage(inquiry: Inquiry, rental: Rental, guest: Guest): MessageForm {
    let quote = ''
    const code = R.pathOr('USD', ['currency', 'code'], rental)
    const currencyPipe = new CurrencyPipe('en')

    if (inquiry.bookingEngine) {
      const cost = inquiry.bookingEngine
      quote = `
        Base Charge: ${currencyPipe.transform(cost.extraBedroomsBase, code, 'symbol')}
        Additional Guests Fee: ${currencyPipe.transform(cost.extraGuests, code, 'symbol')}
        Additional Bedrooms Fee: ${currencyPipe.transform(cost.extraBedrooms, code, 'symbol')}
        Discounts & Fees: ${currencyPipe.transform(cost.discountTotal, code, 'symbol')}
        ${cost.bookingFees?.map((t) => `${t.name}: ${currencyPipe.transform(t.amount, code, 'symbol')}`).join('\n')}
        Taxes: ${currencyPipe.transform(cost.taxTotal, code, 'symbol')}
        Total: ${currencyPipe.transform(cost.total, code, 'symbol')}
      `
    } else {
      const cost = parseBookingCostString(inquiry.cost)
      quote = `
        Base Charge: ${currencyPipe.transform(cost.charge, code, 'symbol')}
        Additional Guests Fee: ${currencyPipe.transform(cost.extraGuests, code, 'symbol')}
        Discounts: ${currencyPipe.transform(cost.discountSum, code, 'symbol')}
        Fees: ${currencyPipe.transform(cost.feeSum, code, 'symbol')}
        Taxes: ${currencyPipe.transform(cost.taxFee, code, 'symbol')}
        Total: ${currencyPipe.transform(cost.total, code, 'symbol')}
      `
    }

    quote = `
      Hi ${guest?.name || inquiry.guestDetails?.name},
      Below is your booking quote:

      ${quote}
    `
    return {
      subject: `Booking Quote - ${rental.name}`,
      cc: [],
      bcc: [],
      messageHtml: quote.replace(/\n/g, '<br>'),
      messageText: quote,
      addCost: false,
      attachments: [],
    }
  }

  quoteMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SendQuoteMessage),
      switchMap(({ inquiry, channel }) =>
        combineLatest([
          this.store.pipe(selectOnce(selectGuestById, { id: inquiry.guestId })),
          this.store.pipe(selectOnce(selectRentalById(inquiry.rentalId))),
        ]).pipe(
          map(([guest, rental]) => this.getBookingQuoteMessage(inquiry, rental, guest)),
          map((form) => AddMessage({ inquiry, form, channel }))
        )
      )
    )
  )

  sendMessages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SendMessageToMultipleChannels),
      switchMap(({ inquiry, form, channels }) => {
        if (!isSomething(channels)) {
          return [AddMessage({ inquiry, form })]
        } else {
          return channels.map((channel) => AddMessage({ inquiry, form, channel }))
        }
      })
    )
  )

  getMessagesByIds$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetMessagesByIds),
      concatMap(({ messageIds }) =>
        this.messageService.getMessagesByIds(messageIds).pipe(
          map((messages) => GetMessagesByIdsComplete({ messages })),
          catchError((error) => of(ActionFailed({ error })))
        )
      )
    )
  )

  getBookingMessages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetBookingMessages),
      concatMap(({ bookingId, background }) =>
        this.messageService.getMessagesByInquiryId(bookingId, background).pipe(
          map((messages) => GetBookingMessagesComplete({ messages, bookingIds: [bookingId] })),
          catchError((error) => of(GetBookingMessagesFailed({ bookingId }), ActionFailed({ error })))
        )
      )
    )
  )

  markAsRead$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MarkAsRead),
      concatMap(({ messageIds }) =>
        this.messageService.markAsRead(messageIds).pipe(
          map(() => {
            return MarkAsReadComplete({ updates: messageIds.map((id) => ({ id, changes: { read: 1 } })) })
          }),
          catchError((error) => of(ActionFailed({ error })))
        )
      )
    )
  )

  markAsUnread$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MarkAsUnread),
      concatMap(({ messageIds }) =>
        this.messageService.markAsUnread(messageIds).pipe(
          map(() => {
            return MarkAsUnreadComplete({ updates: messageIds.map((id) => ({ id, changes: { read: 0 } })) })
          }),
          catchError((error) => of(ActionFailed({ error })))
        )
      )
    )
  )

  updateMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UpdateMessage),
      concatMap(({ messageId, payload }) =>
        this.messageService.update(messageId, payload).pipe(
          map((message) => UpdateMessageComplete({ update: { id: messageId, changes: message } })),
          catchError((error) => of(ActionFailed({ error })))
        )
      )
    )
  )

  markOneAsRead$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MarkOneMessageAsRead),
      concatMap(({ messageId }) =>
        this.messageService.markAsRead([messageId]).pipe(
          map((res) => MarkOneMessageAsReadComplete({ update: { id: messageId, changes: { read: 1 } } })),
          catchError((error) => of(ActionFailed({ error })))
        )
      )
    )
  )

  removeMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeleteMessage),
      switchMap(({ message, inquiryId, silent }) =>
        this.messageService.remove(message.id).pipe(
          map(() => DeleteMessageComplete({ message, inquiryId })),
          tap(() => !silent && this.toast.success($localize`Message deleted successfully!`)),
          catchError((error) => of(ActionFailed({ error })))
        )
      )
    )
  )

  removeMessages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeleteMessages),
      switchMap(({ items, silent }) =>
        this.messageService.removeBatch(items.map((t) => t.message.id)).pipe(
          map(() => DeleteMessagesComplete({ items })),
          tap(() => !silent && this.toast.success($localize`Messages deleted successfully!`)),
          catchError((error) => of(ActionFailed({ error })))
        )
      )
    )
  )

  removeMessageFromFeed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoveMessageFromFeed),
      switchMap(({ message }) =>
        this.messageService.removeFromFeed(message).pipe(
          map(() => RemoveMessageFromFeedComplete({ message })),
          tap(() => this.toast.success($localize`Message removed successfully`)),
          catchError((error) => of(ActionFailed({ error })))
        )
      )
    )
  )

  removeMessageFromFeeds$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoveMessagesFromFeed),
      switchMap(({ messages }) =>
        this.messageService.removeFromFeedBatch(messages).pipe(
          map(() => RemoveMessagesFromFeedComplete({ messages })),
          tap(() => this.toast.success($localize`Messages removed successfully`)),
          catchError((error) => of(ActionFailed({ error })))
        )
      )
    )
  )

  moveMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MoveMessage),
      switchMap(({ toInquiryId, message }) =>
        this.messageService.move(message.id, toInquiryId).pipe(
          tap(() => this.toast.success($localize`Message moved successfully!`)),
          map(() => MoveMessageComplete({ update: { id: message.id, changes: { inquiryId: toInquiryId } } })),
          catchError((error) => of(ActionFailed({ error })))
        )
      )
    )
  )

  moveMessages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MoveMessagesInConversation),
      switchMap(({ convoId, inquiryId, guestId }) =>
        this.messageService.moveMessagesInConversation(convoId, inquiryId, guestId).pipe(
          map((messages) => MoveMessagesInConversationComplete({ messages })),
          catchError((error) => of(ActionFailed({ error })))
        )
      )
    )
  )

  constructor(
    private actions$: Actions,
    private store: Store<fromRoot.State>,
    private inquiryChargeService: InquiryChargeService,
    private messageService: MessageService,
    private toast: Toaster,
    private auth: AuthService
  ) {}
}
